import { useState } from 'react'
import { useSessao } from '../../../auth/contexto'
import { podeFiscalizar } from '../../../auth/permissoes'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro } from '../../../components/Estados'
import { SomenteConsulta } from '../../../components/SomenteConsulta'
import { CampoDeTexto, CampoDeTextoLongo } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'
import { paraNumero, reais } from '../../../lib/dinheiro'
import { useRegistrarValorDaFatura, useRevogarValorDaFatura } from '../../../api/queries/fiscalizacaoManual'
import type { GlosaEmReais } from '../../../types/chamado'
import { DialogoDeTexto } from './DialogoDeTexto'
import { EscolhaDaOs } from './EscolhaDaOs'

/**
 * O valor faturado da competência — a base sobre a qual o desconto incide.
 *
 * **Sem ele o sistema mostra o percentual e não afirma quantia nenhuma.** É a mesma disciplina do
 * indicador não apurado: um "R$ 0,00" ali seria lido como "nada a descontar", e o que há é "ninguém
 * disse sobre quanto".
 *
 * **Não exige competência aberta.** A nota fiscal costuma existir depois do fechamento, e obrigar a
 * reabrir a competência para lançá-la faria a reabertura — ato registrado, pensado para corrigir
 * apuração — virar rotina administrativa. O valor não muda a apuração; ele a multiplica.
 *
 * @param props a competência e a glosa em reais, quando o valor está registrado
 * @returns o cartão do valor
 */
export function ValorDaFatura({ competencia, glosa }: { competencia: string; glosa: GlosaEmReais | null }) {
  const t = TEXTOS.valorDaFatura
  const { usuario } = useSessao()
  const [retirando, setRetirando] = useState(false)
  const revogar = useRevogarValorDaFatura()
  const podeEscrever = podeFiscalizar(usuario)

  if (!glosa) {
    // O quadro em branco continua sendo informação para quem só consulta — "ninguém registrou o
    // valor deste mês" é metade da razão de abrir esta tela. O que sai é o formulário.
    return podeEscrever ? (
      <Formulario competencia={competencia} />
    ) : (
      <SomenteConsulta titulo={t.semValor.titulo} ajuda={t.semValor.ajuda} />
    )
  }

  return (
    <section className="cartao p-4" aria-label={t.titulo}>
      <h2 className="text-base font-semibold text-slate-900">{t.titulo}</h2>

      <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <Linha rotulo={t.valorFaturado} valor={reais(glosa.valorDaFatura) ?? ''} />
        <Linha rotulo={t.glosaApurada} valor={reais(glosa.glosaApurada) ?? ''} />
        <Linha rotulo={t.glosaAplicada} valor={reais(glosa.glosaAplicada) ?? ''} />
        <Linha rotulo={t.valorFinal} valor={reais(glosa.valorFinal) ?? ''} destacado />
      </dl>

      <p className="mt-3 text-xs text-slate-600">
        {glosa.fundamento} · {t.registradoPor(glosa.registradoPorNome)}
      </p>

      {/* A falha da retirada era MUDA: `revogar` não renderizava nada, então a pessoa clicava,
          confirmava o motivo, o diálogo fechava e a tela continuava idêntica. Sem mensagem, o
          próximo passo natural é tentar de novo — e a leitura é "o sistema travou". Cenário 51. */}
      {revogar.isError && (
        <div className="mt-3">
          <AvisoDeErro erro={revogar.error} titulo={TEXTOS.fiscalizacao.acoes.naoDeuCerto} />
        </div>
      )}

      {podeEscrever && (
        <div className="mt-3">
          <Botao onClick={() => setRetirando(true)} disabled={revogar.isPending}>
            {t.retirar}
          </Botao>
        </div>
      )}

      {retirando && (
        <DialogoDeTexto
          titulo={t.retirar}
          ajuda={t.retirarAjuda}
          rotulo="Por que está sendo retirado"
          aoConfirmar={(motivo) => {
            revogar.mutate({ competencia, motivo })
            setRetirando(false)
          }}
          aoFechar={() => setRetirando(false)}
        />
      )}
    </section>
  )
}

/** Uma linha do quadro. `<dl>` para o leitor de tela ligar rótulo e valor. */
function Linha({ rotulo, valor, destacado }: { rotulo: string; valor: string; destacado?: boolean }) {
  return (
    <div className="flex flex-wrap justify-between gap-x-3 border-b border-slate-100 py-1">
      <dt className="text-slate-600">{rotulo}</dt>
      <dd className={destacado ? 'font-semibold tabular-nums text-slate-900' : 'tabular-nums text-slate-800'}>
        {valor}
      </dd>
    </div>
  )
}

/**
 * O registro do valor, quando ele ainda não existe.
 *
 * A ordem de serviço entra aqui como **procedência**: ela diz de onde o valor veio, e não muda o
 * cálculo. O desconto continua incidindo sobre um valor único do mês, com um teto único de 10% — a
 * decisão nº 6 do dono não foi tocada, e `RedutorDaFatura` continua com um teto só.
 */
function Formulario({ competencia }: { competencia: string }) {
  const t = TEXTOS.valorDaFatura
  const registrar = useRegistrarValorDaFatura()
  const [valor, setValor] = useState('')
  const [fundamento, setFundamento] = useState('')
  const [ordemDeServicoId, setOrdemDeServicoId] = useState<number | null>(null)

  // `Number(valor.replace(',', '.'))` trocava só a PRIMEIRA vírgula e não sabia nada de milhar.
  // Cenário que isto fecha: Beatriz digita a nota de agosto como se escreve em reais, "180.000".
  // Sem vírgula o replace não fazia nada, `Number("180.000")` era 180, o botão habilitava, o POST
  // mandava 180 — e o servidor só exige `@Positive`. A glosa de 10% saía R$ 18,00 no quadro de
  // recebimento que vai assinado para o processo. Pior: a grafia COMPLETA, "219.444,44", virava
  // "219.444.44" → NaN → botão desabilitado; a forma certa era recusada e a errada passava.
  const numero = paraNumero(valor)
  const valido = Number.isFinite(numero) && numero > 0 && fundamento.trim().length > 0

  return (
    <section className="cartao space-y-3 p-4" aria-label={t.titulo}>
      <h2 className="text-base font-semibold text-slate-900">{t.semValor.titulo}</h2>
      <p className="max-w-prose text-sm text-slate-600">{t.semValor.ajuda}</p>

      <CampoDeTexto
        rotulo={t.valorFaturado}
        ajuda="O valor da nota fiscal do serviço, em reais."
        inputMode="decimal"
        value={valor}
        onChange={(e) => setValor(e.target.value)}
      />
      <CampoDeTextoLongo
        rotulo="De onde veio este valor"
        ajuda={t.fundamentoAjuda}
        rows={2}
        value={fundamento}
        onChange={(e) => setFundamento(e.target.value)}
      />

      <EscolhaDaOs competencia={competencia} escolhida={ordemDeServicoId} aoEscolher={setOrdemDeServicoId} />

      {registrar.isError && <AvisoDeErro erro={registrar.error} />}

      <Botao
        aparencia="primario"
        disabled={!valido || registrar.isPending}
        onClick={() =>
          registrar.mutate({ competencia, valor: numero, fundamento: fundamento.trim(), ordemDeServicoId })
        }
      >
        {t.registrar}
      </Botao>
    </section>
  )
}
